import {useCallback} from 'react';
import classnames from 'classnames';
import * as store from '@/store';
import {
    PreviewImageAmplifySvg,
    PreviewImageReduceSvg,
    PreviewImageRotateSvg,
    PreviewImageSizeLargeSvg,
    PreviewImageSizeOriginSvg,
} from '@/icons';
import Icon, {IconSize} from '@/components/Icon';
import styles from './index.module.scss';
import Button from './Button';
import Spliter from './Spliter';

export interface ImageViewerProps {
    previewImage: any;
    previewImageVisible: boolean;
    previewImageRatio: number;
    previewImageAdaptionRatio: number;
    previewImageMode: string;
}

const VALID_RATIOS = [10, 25, 50, 75, 100, 125, 150, 175, 200, 225, 250, 275, 300, 325, 350, 375, 400];
const findClosestRatio = (adaptionRatio: number, direction: 'smaller'|'larger') => {
    if (direction === 'smaller') {
        for (let i = 0; i < VALID_RATIOS.length; i++) {
            const ratio = VALID_RATIOS[i];
            if (ratio > adaptionRatio) {
                return i > 0 ? VALID_RATIOS[i - 1] : VALID_RATIOS[0];
            }
        }
    }
    else if (direction === 'larger') {
        return VALID_RATIOS.find(el => el > adaptionRatio) || VALID_RATIOS[VALID_RATIOS.length - 1];
    }
};

const PreToolbar = (props: ImageViewerProps) => {
    const {
        previewImage: viewer,
        previewImageVisible: visible = false,
        previewImageRatio: ratio,
        previewImageAdaptionRatio: adaptionRatio,
        previewImageMode: previewMode,
    } = props;

    // 缩小
    const handleScaleDown = useCallback(() => {
        if (ratio > adaptionRatio && ratio - adaptionRatio < 25) {
            viewer.zoomTo(adaptionRatio / 100);
        }
        else if (ratio === adaptionRatio) {
            // 缩小到临近的固定比例
            const zoomValue: any = findClosestRatio(adaptionRatio, 'smaller');
            viewer.zoomTo(zoomValue / 100);
        }
        else if (ratio > 375) {
            viewer.zoomTo(3.75);
        }
        else if (ratio <= 25) {
            viewer.zoomTo(0.1);
        }
        else {
            viewer.zoomTo(Math.round(ratio / 25) * 0.25 - 0.25);
        }
    }, [viewer, ratio, adaptionRatio]);

    // 放大
    const handleScaleUp = useCallback(() => {
        if (ratio < adaptionRatio
            && (ratio > 10 && adaptionRatio - ratio < 25
                || ratio === 10 && adaptionRatio - ratio < 15)) {
            viewer.zoomTo(adaptionRatio / 100);
        }
        else if (ratio === adaptionRatio) {
            const zoomValue: any = findClosestRatio(adaptionRatio, 'larger');
            viewer.zoomTo(zoomValue / 100);
        }
        else if (ratio >= 375) {
            viewer.zoomTo(4);
        }
        else if (ratio < 25) {
            viewer.zoomTo(0.25);
        }
        else {
            viewer.zoomTo(Math.round(ratio / 25) * 0.25 + 0.25);
        }
    }, [viewer, ratio, adaptionRatio]);

    // 调整预览模式
    const togglePreviewMode = useCallback(() => {
        previewMode === 'origin' ? viewer.zoomTo(1) : viewer.zoomTo(adaptionRatio / 100);
        store.setPreviewImageMode(previewMode === 'origin' ? 'large' : 'origin');
    }, [previewMode, viewer, adaptionRatio]);

    // 旋转
    const handleRotate = useCallback(() => {
        viewer.rotate(-90);
    }, [viewer]);

    return (
        <div
            className={classnames(
                styles.previewToolbar,
                !visible && styles.previewToolbarHidden
            )}
        >
            <Button
                tooltip="缩小"
                disabled={ratio <= 10}
                onClick={handleScaleDown}
            >
                <Icon svg={PreviewImageReduceSvg} size={IconSize.XXL} />
            </Button>
            <div className={styles.imageRatio}>
                {ratio}%
            </div>
            <Button
                tooltip="放大"
                disabled={ratio >= 400}
                onClick={handleScaleUp}
                style={{
                    marginRight: '12px',
                }}
            >
                <Icon svg={PreviewImageAmplifySvg} size={IconSize.XXL} />
            </Button>
            <Button
                tooltip={previewMode === 'origin' ? '原始尺寸' : '适应页面'}
                onClick={togglePreviewMode}
            >
                <Icon
                    svg={
                        previewMode === 'origin' ?
                            PreviewImageSizeOriginSvg :
                            PreviewImageSizeLargeSvg
                    }
                    size={IconSize.XXL}
                />
            </Button>
            <Spliter />
            <Button
                tooltip={'旋转'}
                onClick={handleRotate}
            >
                <Icon svg={PreviewImageRotateSvg} size={IconSize.XXL} />
            </Button>
        </div>
    );
};


export default store.connect([
    store.KEYS.PREVIEW_IMAGE,
    store.KEYS.PREVIEW_IMAG_VISIBLE,
    store.KEYS.PREVIEW_IMAGE_RATIO,
    store.KEYS.PREVIEW_IMAGE_ADAPTION_RATIO,
    store.KEYS.PREVIEW_IMAGE_MODE,
])(PreToolbar);
